import React, { useState, useEffect, useRef } from 'react';
import { Text, Title, Container, Box, Grid } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconDeviceWatch, IconFlask, IconClipboardCheck, IconBrain } from '@tabler/icons-react';
import EcgLine from '../components/EcgLine';
import './Home.css';

const PINK = '#d6336c';

function Home() {
  const ecgWrapRef = useRef(null);
  const [ecgSize, setEcgSize] = useState({ width: 800, height: 320 });

  useEffect(() => {
    const el = ecgWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setEcgSize({ width: el.offsetWidth, height: el.offsetHeight });
    });
    ro.observe(el);
    setEcgSize({ width: el.offsetWidth, height: el.offsetHeight });
    return () => ro.disconnect();
  }, []);

  return (
    <Box className="home-page">
      <section className="home-ecg-section" ref={ecgWrapRef}>
        <EcgLine
          width={ecgSize.width}
          height={ecgSize.height}
          color="#d6336c"
          className="home-ecg-canvas"
        />
      </section>

      <section className="home-hero">
        <Container size="lg" className="home-hero-inner">
          <Title order={1} className="home-title">
            Advancing women's health research with data
          </Title>
        </Container>
      </section>

      {/* Three white boxes in one row */}
      <Container size="lg" className="home-features">
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <div className="home-box">
              <div className="home-feature-icon">
                <IconDeviceWatch size={28} color={PINK} stroke={1.75} />
              </div>
              <Title order={2} className="home-feature-title">
                <Link to="/wearables" className="home-feature-link">Wearables</Link>
              </Title>
              <Text className="home-feature-text">
                Your wearable device has a wealth of untapped data on metrics like heart rate, sleep quality, recovery scores, and activity levels that are hard to manually track.
                We provide you the tools to pull your data from multiple wearable devices.
              </Text>
            </div>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <div className="home-box">
              <div className="home-feature-icon">
                <IconFlask size={28} color={PINK} stroke={1.75} />
              </div>
              <Title order={2} className="home-feature-title">
                <Link to="/lab-results" className="home-feature-link">Lab Results</Link>
              </Title>
              <Text className="home-feature-text">
                Upload your blood test result PDFs, and we'll extract the data for you. You can then track how your results change over time, and explore how they're correlated with wearable data. 
              </Text>
            </div>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <div className="home-box">
              <div className="home-feature-icon">
                <IconClipboardCheck size={28} color={PINK} stroke={1.75} />
              </div>
              <Title order={2} className="home-feature-title">
                <Link to="/questionnaires" className="home-feature-link">Questionnaires</Link>
              </Title>
              <Text className="home-feature-text">
                We've worked with women's health professionals to design a questionnaire capturing female-specific health metrics that are often missed, yet shown to be highly predictive of health outcomes.
                We made this questionnaire as simple and as short as possible, so you can fill it out in a few minutes.
              </Text>
            </div>
          </Grid.Col>
        </Grid>
      </Container>

      {/* Bottom white box – advancing ML for women's health */}
      <Container size="md" className="home-research-wrap">
        <div className="home-box home-box--research">
          <div className="home-research-icon">
            <IconBrain size={32} color={PINK} stroke={1.75} />
          </div>
          <Title order={2} className="home-research-title">
            Advancing Machine Learning for Women's Health
          </Title>
          <Text className="home-research-text">
            Open Health is built with the goal of collecting data for large-scale machine learning studies on women's health.
            There's currently a major gender gap in health data availability, with many important female metrics, such as menstrual cycle, menopause, and pregnancy tracking, not collected in large-scale efforts.
            We intend to help close this gap by gathering important data points you have at your fingertips (e.g. wearable data, lab results, and questionnaires) and releasing them to researchers for large-scale machine learning analyses.
            You will be given the option to opt-in or opt-out of releasing your data for these studies.
            We hope you feel excited about the prospect of contributing to the advancement of women's health research!
          </Text>
        </div>
      </Container>
    </Box>
  );
}

export default Home;
